import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { Event } from '@feathr/blackbox';
import { useLicense } from '@feathr/extender/hooks';
import { useAccount, useActionBar, useLocalUrl, useStore } from '@feathr/extender/state';

import type { IProjectSettingsFields } from './useProjectSettingsFields';
import { useProjectSettingsFields } from './useProjectSettingsFields';

/** Return type for the useEventSettingsInfo hook. */
interface IUseEventSettingsInfoReturn {
  /** The current project (event) model. */
  event: Event;
  /** State and handlers for the settings fields. */
  fields: IProjectSettingsFields;
  /** Whether the account has the invites license (gates the invite page domain field). */
  hasInvites: boolean;
  /** Whether the project model has unsaved changes. */
  isDirty: boolean;
  /** Builds an account-prefixed URL. */
  localUrl: ReturnType<typeof useLocalUrl>;
  /** Action bar setter for right-aligned actions (the save button). */
  setRightActions: ReturnType<typeof useActionBar>['setRightActions'];
  /** Translation function. */
  t: TFunction;
}

/** Encapsulates state and business logic for the Project Settings Info page. */
export function useEventSettingsInfo(): IUseEventSettingsInfoReturn {
  const account = useAccount();
  const { Events, RedirectDomains } = useStore();
  const { eventId } = useParams<{ eventId: string }>();
  const localUrl = useLocalUrl();
  const { t } = useTranslation();
  const { setRightActions } = useActionBar();
  const hasInvites = useLicense('invites');

  const event = Events.get(eventId);
  const fields = useProjectSettingsFields({ account, event, redirectDomains: RedirectDomains });

  return {
    event,
    fields,
    hasInvites,
    isDirty: event.isDirty,
    localUrl,
    setRightActions,
    t,
  };
}
